/**
 * People and passkeys (docs/members.md "People and passkeys"): the pure parts the server and both
 * clients share. Who a device claims when it adds a passkey, how a passkey's device is described,
 * how the house counts its stewards once stewardship belongs to people, and the challenge rows'
 * keys and expiry. Imports only shared/members.ts: this file runs in a Pages Function, the Preact
 * app, the native app and Node's test runner.
 */
import { higherRole, leavesNoSteward, roleRank, type Role } from "./members.ts";

/** What the server says when a passkey route refuses, and what the clients show as is. */
export const PASSKEY_MESSAGES = {
  wrongHost: "Passkeys live at omiespeons.ca",
  pickName: "Pick a peon name first",
  nameHeld: "Someone already holds that name here. Sign in with their passkey first, or pick another name.",
  stewardName: "A steward holds that name here. Pick another name.",
  unknown: "That passkey is not known here",
  notVerified: "That did not verify",
  alreadyHere: "That passkey is already here",
  expired: "Took too long, try again",
  noSuchPasskey: "That passkey is not in this household",
  notYours: "Only a steward or its owner can remove a passkey",
  badIntent: "intent must be register or login",
} as const;

// ---------- Claiming ----------

/**
 * A person as the claiming rule needs one: its id, role, how many passkeys it holds and whether it
 * holds a password (docs/members.md "Personal passwords").
 */
export interface ClaimPerson {
  id: string;
  role: Role;
  passkeys: number;
  password: boolean;
}

/**
 * Whether a person holds any credential (a passkey or a password). A person who does holds their
 * name: no other device can claim it or use it without signing in as them.
 */
export function holdsCredentials(p: Pick<ClaimPerson, "passkeys" | "password">): boolean {
  return p.passkeys > 0 || p.password;
}

/**
 * Who a device becomes when it adds a passkey or sets a password. `self`: the session is already
 * bound to a person (a second passkey, or a new password, for yourself). `create`: a new person
 * with the name and the session's role. `bind`: an existing person with no credentials (no passkey
 * and no password) takes this session, at the higher of the two roles.
 * `taken` and `steward` refuse (409), `noName` asks for a name first (400).
 */
export type ClaimDecision =
  | { kind: "self"; personId: string }
  | { kind: "create"; name: string; role: Role }
  | { kind: "bind"; personId: string; role: Role }
  | { kind: "taken" }
  | { kind: "steward" }
  | { kind: "noName" };

/**
 * The claiming rule, given this device's session (its role and the person it is bound to, if any),
 * the peon name it claims (already normalized; '' for none) and the person of that name in the
 * household, if one exists.
 *
 * 1. Bound to a person: that person, whatever the name.
 * 2. No name: refused.
 * 3. Nobody of that name: a new person with the session's role.
 * 4. A person of that name with no credentials: bound, at the higher role. Except that a peon may
 *    not take a steward's name this way (a name is free to type, so that would make any peon a
 *    steward whose credentials had all been removed): refused with its own words.
 * 5. A person of that name holding credentials (a passkey or a password): refused; sign in as them
 *    instead.
 */
export function claimFor(session: { role: Role; personId: string | null }, name: string, named: ClaimPerson | null): ClaimDecision {
  if (session.personId) return { kind: "self", personId: session.personId };
  if (!name) return { kind: "noName" };
  if (!named) return { kind: "create", name, role: session.role };
  if (holdsCredentials(named)) return { kind: "taken" };
  if (roleRank(session.role) < roleRank(named.role)) return { kind: "steward" };
  return { kind: "bind", personId: named.id, role: higherRole(named.role, session.role) };
}

// ---------- Describing a passkey ----------

/**
 * A short word for the device a passkey was made on, from the User-Agent at registration (never the
 * whole string): "iPhone", "iPad", "Mac", "Android", "Windows", "ChromeOS", "Linux", or, when the
 * user agent says nothing useful (the native apps' HTTP clients), the platform the client reported.
 * '' when neither says.
 */
export function passkeyLabel(userAgent: string, platform = ""): string {
  const ua = userAgent;
  if (/iPhone|iPod/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android";
  if (/CrOS/.test(ua)) return "ChromeOS";
  if (/Windows/.test(ua)) return "Windows";
  if (/Macintosh|Mac OS X/.test(ua)) return "Mac";
  if (/Linux/.test(ua)) return "Linux";
  if (platform === "ios") return "iPhone";
  if (platform === "android") return "Android";
  return "";
}

// ---------- Counting stewards ----------

/**
 * The stewards the "house needs a steward" rule counts: every person (whatever devices it has
 * signed in) and every session not bound to a person. A session bound to a person counts as that
 * person, so signing one of its devices out never removes a steward, and demoting one demotes the
 * person. Each unit's id is `p:<person id>` or `s:<session id>`.
 */
export function stewardUnits(
  members: ReadonlyArray<{ session_id: string; role: Role; person?: { id: string } | null }>,
  people: ReadonlyArray<{ id: string; role: Role }>,
): Array<{ session_id: string; role: Role }> {
  const units = new Map<string, Role>();
  for (const p of people) units.set(`p:${p.id}`, p.role);
  for (const m of members) {
    if (m.person) {
      if (!units.has(`p:${m.person.id}`)) units.set(`p:${m.person.id}`, m.role);
    } else units.set(`s:${m.session_id}`, m.role);
  }
  return [...units].map(([session_id, role]) => ({ session_id, role }));
}

/** The unit a session's change touches: its person when bound, else the session itself. */
export function stewardUnitOf(member: { session_id: string; person?: { id: string } | null }): string {
  return member.person ? `p:${member.person.id}` : `s:${member.session_id}`;
}

/**
 * Whether a change leaves the house with no steward, counting people and unbound sessions
 * (stewardUnits). A role change on a bound session changes its person; signing a bound session
 * out removes no one (the person keeps its role and passkeys).
 */
export function leavesNoStewardWithPeople(
  members: ReadonlyArray<{ session_id: string; role: Role; person?: { id: string } | null }>,
  people: ReadonlyArray<{ id: string; role: Role }>,
  target: string,
  next: Role | "removed",
): boolean {
  const member = members.find((m) => m.session_id === target);
  if (!member) return false;
  if (member.person && next === "removed") return false;
  return leavesNoSteward(stewardUnits(members, people), stewardUnitOf(member), next);
}

// ---------- Challenges ----------

/** How long a passkey challenge is good for. */
export const CHALLENGE_TTL_MS = 5 * 60 * 1000;

export type PasskeyIntent = "register" | "login";

export function isPasskeyIntent(v: unknown): v is PasskeyIntent {
  return v === "register" || v === "login";
}

/**
 * A challenge row's value, stored as JSON in the `meta` table under `passkey:<challenge id>`. For
 * a registration, the session and household that asked, the person it will claim (an existing one,
 * or the id minted for a new one) and the name it claims; for a sign-in, only the challenge.
 */
export interface PasskeyChallenge {
  challenge: string;
  intent: PasskeyIntent;
  session_id: string | null;
  household_id: string | null;
  person_id: string | null;
  /** The peon name a new person takes (registration, rule "create"); '' otherwise. */
  name: string;
  expires_at: string;
}

export const CHALLENGE_KEY_PREFIX = "passkey:";

/** A challenge id: 32 hex characters. */
export function isChallengeId(v: unknown): v is string {
  return typeof v === "string" && /^[0-9a-f]{32}$/.test(v);
}

export function challengeKey(id: string): string {
  return `${CHALLENGE_KEY_PREFIX}${id}`;
}

/** A stored challenge value back as a PasskeyChallenge, or null for anything that is not one. */
export function parseChallenge(value: unknown): PasskeyChallenge | null {
  if (typeof value !== "string") return null;
  let v: unknown;
  try {
    v = JSON.parse(value);
  } catch {
    return null;
  }
  if (!v || typeof v !== "object") return null;
  const c = v as Record<string, unknown>;
  if (typeof c.challenge !== "string" || !isPasskeyIntent(c.intent) || typeof c.expires_at !== "string") return null;
  const str = (x: unknown) => (typeof x === "string" ? x : null);
  return {
    challenge: c.challenge,
    intent: c.intent,
    session_id: str(c.session_id),
    household_id: str(c.household_id),
    person_id: str(c.person_id),
    name: typeof c.name === "string" ? c.name : "",
    expires_at: c.expires_at,
  };
}

/** Whether a challenge is past its time (an unreadable time counts as past). */
export function challengeExpired(c: Pick<PasskeyChallenge, "expires_at">, now = Date.now()): boolean {
  const t = Date.parse(c.expires_at);
  return Number.isNaN(t) || t <= now;
}
